
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { findSupportedLocale } from '@/utils/i18n-helpers';
import { scrollIntoViewIfNeeded } from '@/utils/helpers';
import { validateStudentFields, getSchoolDeliveryOptions } from '@/utils/helpers';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';

const namespace = 'auth';

@Component({
	components: { ToggleSwitch, ValidationProvider }
})
export default class DeliverySchool extends Vue {
	@Prop({ type: Object, required: true, default: () => {} }) private restaurantDeliveryInfo!: SchoolDelivery;
	@Prop({ type: Object, default: () => ({}) }) private deliveryInformation!: CheckoutDeliveryInfo;
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('getUserStudents', { namespace }) private students!: UserStudent[];
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;

	private delivery: CheckoutDeliveryInfo = { type: 'school' };
	private schools: (string | undefined)[] | null = [];
	private isNewStudent: boolean = false;
	private saveStudentInformation: boolean = false;

	/**
	 * This getter fetches the school delivery information.
	 * It is used to populate the school's dependant fields
	 *
	 * @return {SchoolDeliveryOptions}
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions {
		return getSchoolDeliveryOptions(this.restaurantDeliveryInfo, this.delivery.school);
	}

	private get studentOptions(): string[] {
		return this.students?.map(student => student.name || '').filter(Boolean);
	}

	/**
	 * Set schools in the selectors
	 *
	 * @return {void}
	 */
	private created(): void {
		this.schools = this.restaurantDeliveryInfo.schools?.map((school) => school.name);
	}

	/**
	 * Fetch this user's students
	 * Reset the delivery info
	 * Update school to default school if only one school is available
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if (this.deliveryInformation) {
			this.delivery = { ...this.deliveryInformation, type: 'school' };
		}
		if(this.restaurantDeliveryInfo.schools.length === 1 && this.delivery.school === undefined) {
			this.delivery.school = this.restaurantDeliveryInfo.schools[0].name;
		}
	}

	/**
	 * Checks if the user wants to create this new Student and adds it to their list of students.
	 * input = True: Create new student
	 * input = False: Don't create new student
	 *
	 * @param {boolean} newValue
	 * @return {void}
	 */
	private createNewStudentToggle(newValue: boolean): void {
		this.$emit('create-delivery-info', newValue);
		this.saveStudentInformation = newValue;
	}

	/**
	 * This function should only be used for logged in users.
	 * On student change, we have two cases
	 * Student exists (Is already part of the user's list of students): populate that student's values in the input fields
	 * Student doesn't exist (Is a new student): empty all the input fields and show the "Create student" toggle input.
	 *
	 * @return {void}
	 */
	private onStudentChange(): void {
		if (this.user?.id){
			const studentName = this.delivery.student;
			const existing = this.students.find(student => student.name === studentName);
			if (existing) {
				this.delivery = { ...existing, type: 'school', name: studentName, student: studentName };
				this.isNewStudent = false;
				this.createNewStudentToggle(false);
				validateStudentFields({ student: this.delivery, restaurantDeliveryInfo: this.restaurantDeliveryInfo, schoolDeliveryOptions: this.schoolDeliveryOptions });
			}
			else {
				this.delivery = {
					id: '',
					type: 'school',
					name: studentName,
					student: studentName,
					school: '',
					grade: '',
					classroom: '',
					teacher: ''
				};
				this.isNewStudent = true;
				this.createNewStudentToggle(true);
			}
			this.updateDeliveryInfo();
		}
	}

	/**
	 * When we change the school, we empty the school-related inputs.
	 * We then populate that school's related values: grades, classrooms, teachers
	 * Teachers could be a select or a free form text, depending on the school.
	 *
	 * @return {void}
	 */
	private onSchoolChange(): void {
		this.delivery.grade = '';
		this.delivery.classroom = '';
		this.delivery.teacher = '';
		this.updateDeliveryInfo();
	}

	/**
	 * Send event to the parent to update the delivery
	 * information in the vuex store
	 *
	 * @return {void}
	 */
	private updateDeliveryInfo(): void {
		// If the restaurant has locales, set address with localization
		if (this.restaurant.locales?.length && this.restaurant.locales.length > 1) {
			this.restaurant.locales.forEach((locale: RestaurantLocale) => {
				const supportedLocale: Locale | undefined = findSupportedLocale('', locale.locale_short);
				// Set the default locale as the address
				if (supportedLocale && locale.is_default) {
					this.delivery.address = this.buildAddress(supportedLocale.code);
				}
				// Set the address for the other supported locales
				else if (supportedLocale) {
					this.delivery.localization = {
						...this.delivery.localization,
						[locale.locale_short]: {
							address: this.buildAddress(supportedLocale.code)
						}
					};
				}
			});
		}
		// If no locale, just set the address
		else {
			this.delivery.address = this.buildAddress();
		}

		this.$emit('input', this.delivery);
	}

	/**
	 * Build the address string for the delivery object, in the correct locale if applicable.
	 * Add each school delivery field together, depending on which fields are in the delivery object.
	 *
	 * @param {string} [localeCode]
	 * @return {string} Output example: 'School: Mushroom Kingdom, Grade: 1, Classroom: A, Teacher: Mario, Student: Luigi'
	 */
	private buildAddress(localeCode?: string): string {
		const schoolFields: (keyof CheckoutDeliveryInfo)[] = ['school', 'grade', 'classroom', 'teacher', 'student'];
		const address: string[] = [];

		schoolFields.forEach((field) => {
			const value = this.delivery[field];
			if (value) {
				const localizedAddress = localeCode
					? this.$t(`checkout.form.delivery.school.${field}_address`, localeCode, { [field]: value })
					: this.$t(`checkout.form.delivery.school.${field}_address`, { [field]: value });
				address.push(localizedAddress);
			}
		});

		return address.join(', ');
	}

	/**
	 * Scroll to the bottom when opening a selector
	 *
	 * @param {string} elementId
	 * @return {void}
	 */
	private opened(elementId: string): void {
		setTimeout(() => {
			let contentDiv = document.querySelector(`#${elementId} .vs__dropdown-menu`);
			contentDiv && scrollIntoViewIfNeeded(contentDiv, { behavior: 'smooth', block: 'end', inline: 'start' });
		}, 100);
	}
}
